import { useState } from 'react';
import { useSubscriptionStore } from '../stores/subscription.store';
import { toast } from 'react-hot-toast';
import { AlertTriangle } from 'lucide-react';
import { logger } from '@/features/help/utils/logger';

interface TrialEndedModalProps {
  isOpen: boolean;
}

/**
 * Modal component displayed when free trial has ended
 */
export const TrialEndedModal = ({ isOpen }: TrialEndedModalProps) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const { createSubscription, freeTrialLimit, freeUsageCount } = useSubscriptionStore();

  if (!isOpen) return null;

  // Handle subscription creation
  const handleUpgrade = async () => {
    try {
      setIsProcessing(true);
      logger.info('Subscription', 'Initiating upgrade from trial ended modal');
      const checkoutUrl = await createSubscription();
      window.location.href = checkoutUrl;
    } catch (error) {
      logger.error('Subscription', 'Failed to create checkout session from trial modal', {
        error,
      });
      toast.error('Failed to create subscription. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      <div className="fixed inset-0 bg-black bg-opacity-75 transition-opacity"></div>

      <div className="bg-white rounded-lg shadow-xl p-6 mx-4 md:mx-auto max-w-md w-full z-10 relative">
        <div className="absolute top-4 right-4">
          {/* No close button - user must upgrade */}
          <AlertTriangle className="h-6 w-6 text-yellow-600" />
        </div>

        <div className="space-y-6">
          <div className="text-center space-y-3">
            <h3 className="text-xl font-bold text-gray-900">Free Trial Ended</h3>
            <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
              <p className="text-sm text-yellow-800">
                You've used {freeUsageCount} of your {freeTrialLimit} free application submissions.
                Upgrade to Pro to continue processing applications.
              </p>
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg space-y-3">
            <h4 className="font-medium text-gray-900">Pro Plan Benefits</h4>
            <ul className="text-sm text-gray-600 space-y-2">
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span>Unlimited application submissions</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span>Advanced bank statement analysis</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span>Priority support</span>
              </li>
            </ul>
          </div>

          <button
            onClick={handleUpgrade}
            disabled={isProcessing}
            className="w-full py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
          >
            {isProcessing ? 'Processing...' : 'Upgrade to Pro'}
          </button>
        </div>
      </div>
    </div>
  );
};
